import React from 'react';
import CustomIcon from '../Common/CustomIcon/CustomIcon';
import { getSession } from 'Vulture/Session';
import { broncoURL } from 'Vulture/ENV.js';

import style from './UserProfile.module.css';

const EXPIRY_OPTIONS = [
	{ value: '1d',   label: '1 Day' },
	{ value: '7d',   label: '7 Days' },
	{ value: '30d',  label: '30 Days' },
	{ value: '90d',  label: '3 Months' },
	{ value: '180d', label: '6 Months' },
];

const API_RESOURCES = [
	{ key: 'components',  label: 'Components' },
	{ key: 'listings',    label: 'Listings' },
	{ key: 'systems',     label: 'Systems' },
	{ key: 'templates',   label: 'Templates' },
	{ key: 'slideshows',  label: 'Slideshows' },
	{ key: 'wayfinding',  label: 'Wayfinding' },
	{ key: 'folders',     label: 'Folders' },
	{ key: 'settings',    label: 'Settings' },
	{ key: 'accounts',    label: 'Accounts' },
	{ key: 'users',       label: 'Users' },
	{ key: 'messaging',   label: 'Messaging' },
];

const API_OPERATIONS = ['read', 'create', 'update', 'delete'];

class UserProfile extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			activeTab:     'password',
			// Password
			currentPass:   '',
			newPass:       '',
			confirmPass:   '',
			passLoading:   false,
			passSuccess:   '',
			passError:     '',
			// Tokens
			tokens:         [],
			tokensLoading:  true,
			// Create modal
			showCreate:        false,
			newTokenName:      '',
			newTokenExpiry:    '30d',
			selectedPerms:     {},
			createLoading:     false,
			createError:       '',
			// Token reveal modal
			revealToken:       null,
			// General
			error:             '',
		};
	}

	componentDidMount() {
		this.fetchTokens();
	}

	apiRequest(path, opts = {}) {
		const session = getSession();
		const defaults = {
			headers: {
				'Content-Type':   'application/json',
				'X-Auth-Session': session.session,
			},
		};
		return fetch(`${broncoURL}${path}`, { ...defaults, ...opts })
			.then((res) => {
				if (!res.ok) {
					return res.json().then((d) => {
						throw new Error(d.error || res.statusText);
					});
				}
				return res.json();
			});
	}

	fetchTokens() {
		this.apiRequest('/api/tokens', { method: 'GET' })
			.then((data) => {
				this.setState({ tokens: data.tokens || [], tokensLoading: false });
			})
			.catch((err) => {
				this.setState({ error: err.message, tokensLoading: false });
			});
	}

	handleChangePassword() {
		const { currentPass, newPass, confirmPass } = this.state;

		if (!currentPass || !newPass) {
			this.setState({ passError: 'All fields are required.' });
			return;
		}
		if (newPass !== confirmPass) {
			this.setState({ passError: 'New passwords do not match.' });
			return;
		}

		this.setState({ passLoading: true, passError: '', passSuccess: '' });

		this.apiRequest('/api/user/change-password', {
			method: 'POST',
			body:   JSON.stringify({
				current_password: currentPass,
				new_password:     newPass,
			}),
		})
			.then(() => {
				this.setState({
					passLoading: false,
					passSuccess: 'Password changed successfully.',
					currentPass: '',
					newPass:     '',
					confirmPass: '',
				});
			})
			.catch((err) => {
				this.setState({ passLoading: false, passError: err.message });
			});
	}

	handleCreateToken() {
		const { newTokenName, newTokenExpiry, selectedPerms } = this.state;
		const perms = Object.keys(selectedPerms).filter((k) => selectedPerms[k]);

		if (!newTokenName) {
			this.setState({ createError: 'Display name is required.' });
			return;
		}
		if (perms.length === 0) {
			this.setState({ createError: 'Select at least one permission.' });
			return;
		}

		this.setState({ createLoading: true, createError: '' });

		this.apiRequest('/api/tokens', {
			method: 'POST',
			body:   JSON.stringify({
				display_name: newTokenName,
				permissions:  perms,
				expires_in:   newTokenExpiry,
			}),
		})
			.then((data) => {
				this.setState({
					createLoading: false,
					showCreate:    false,
					revealToken:   data.token,
					newTokenName:  '',
					newTokenExpiry: '30d',
					selectedPerms: {},
				});
				this.fetchTokens();
			})
			.catch((err) => {
				this.setState({ createLoading: false, createError: err.message });
			});
	}

	handleRevokeToken(tokenUUID) {
		if (!window.confirm('Revoke this token? This cannot be undone.')) return;

		this.apiRequest(`/api/tokens/${tokenUUID}`, { method: 'DELETE' })
			.then(() => {
				this.fetchTokens();
			})
			.catch((err) => {
				this.setState({ error: err.message });
			});
	}

	getPasswordRules() {
		const { newPass } = this.state;
		if (!newPass) return null;

		const rules = [
			{ label: 'At least 8 characters',              pass: newPass.length >= 8 },
			{ label: 'One uppercase letter',                pass: /[A-Z]/.test(newPass) },
			{ label: 'One lowercase letter',                pass: /[a-z]/.test(newPass) },
			{ label: 'One number',                          pass: /[0-9]/.test(newPass) },
			{ label: 'One special character (!@#$%^&*)',    pass: /[!@#$%^&*]/.test(newPass) },
		];
		return rules;
	}

	toggleResourceRow(resourceKey, checked) {
		this.setState((prev) => {
			const next = { ...prev.selectedPerms };
			API_OPERATIONS.forEach((op) => {
				next[`api:${resourceKey}:${op}`] = checked;
			});
			return { selectedPerms: next };
		});
	}

	toggleOperationCol(op, checked) {
		this.setState((prev) => {
			const next = { ...prev.selectedPerms };
			API_RESOURCES.forEach((r) => {
				next[`api:${r.key}:${op}`] = checked;
			});
			return { selectedPerms: next };
		});
	}

	toggleAllPerms(checked) {
		this.setState((prev) => {
			const next = { ...prev.selectedPerms };
			API_RESOURCES.forEach((r) => {
				API_OPERATIONS.forEach((op) => {
					next[`api:${r.key}:${op}`] = checked;
				});
			});
			return { selectedPerms: next };
		});
	}

	isRowChecked(resourceKey) {
		const { selectedPerms } = this.state;
		return API_OPERATIONS.every((op) => selectedPerms[`api:${resourceKey}:${op}`]);
	}

	isColChecked(op) {
		const { selectedPerms } = this.state;
		return API_RESOURCES.every((r) => selectedPerms[`api:${r.key}:${op}`]);
	}

	isAllChecked() {
		const { selectedPerms } = this.state;
		return API_RESOURCES.every((r) =>
			API_OPERATIONS.every((op) => selectedPerms[`api:${r.key}:${op}`])
		);
	}

	renderPasswordTab() {
		const { currentPass, newPass, confirmPass, passLoading, passSuccess, passError } = this.state;
		const rules = this.getPasswordRules();

		return (
			<div className={style.formSection}>
				{passSuccess && <div className={style.successBanner}>{passSuccess}</div>}
				{passError && <div className={style.errorBanner}>{passError}</div>}

				<div className={style.formField}>
					<label>Current Password</label>
					<input
						type="password"
						value={currentPass}
						onChange={(e) => this.setState({ currentPass: e.target.value })}
						autoComplete="current-password"
					/>
				</div>
				<div className={style.formField}>
					<label>New Password</label>
					<input
						type="password"
						value={newPass}
						onChange={(e) => this.setState({ newPass: e.target.value })}
						autoComplete="new-password"
					/>
					{rules && (
						<ul className={style.passwordRules}>
							{rules.map((r, i) => (
								<li key={i} className={r.pass ? style.rulePass : style.ruleFail}>
									{r.pass ? '✓' : '✗'} {r.label}
								</li>
							))}
						</ul>
					)}
				</div>
				<div className={style.formField}>
					<label>Confirm New Password</label>
					<input
						type="password"
						value={confirmPass}
						onChange={(e) => this.setState({ confirmPass: e.target.value })}
						autoComplete="new-password"
					/>
				</div>
				<button
					className={style.submitBtn}
					disabled={passLoading}
					onClick={() => this.handleChangePassword()}
				>
					{passLoading ? 'Changing...' : 'Change Password'}
				</button>
			</div>
		);
	}

	renderTokensTab() {
		const { tokens, tokensLoading } = this.state;

		return (
			<div>
				<div className={style.header}>
					<span />
					<button
						className={style.submitBtn}
						onClick={() => this.setState({ showCreate: true, createError: '' })}
					>
						Create Token
					</button>
				</div>

				{tokensLoading && (
					<div className={style.emptyState}>
						<CustomIcon icon="spinner" spin={true} /> Loading...
					</div>
				)}

				{!tokensLoading && tokens.length === 0 && (
					<div className={style.emptyState}>No API tokens yet.</div>
				)}

				{!tokensLoading && tokens.length > 0 && (
					<div className={style.tokenList}>
						<div className={style.tokenHeader}>
							<div className={style.colName}>Name</div>
							<div className={style.colPrefix}>Prefix</div>
							<div className={style.colPerms}>Permissions</div>
							<div className={style.colExpiry}>Expires</div>
							<div className={style.colStatus}>Status</div>
							<div className={style.colActions} />
						</div>
						{tokens.map((t) => {
							const isExpired = t.expired || new Date(t.expires_at) < new Date();
							return (
								<div key={t.uuid} className={style.tokenRow}>
									<div className={style.colName}>{t.display_name}</div>
									<div className={style.colPrefix}>avt_{t.token_prefix}...</div>
									<div className={style.colPerms}>{(t.permissions || []).length}</div>
									<div className={style.colExpiry}>
										{new Date(t.expires_at).toLocaleDateString()}
									</div>
									<div className={style.colStatus}>
										<span className={isExpired ? style.statusExpired : style.statusActive}>
											{isExpired ? 'Expired' : 'Active'}
										</span>
									</div>
									<div className={style.colActions}>
										{!isExpired && (
											<button
												className={`${style.actionBtn} ${style.revokeBtn}`}
												onClick={() => this.handleRevokeToken(t.uuid)}
											>
												Revoke
											</button>
										)}
									</div>
								</div>
							);
						})}
					</div>
				)}
			</div>
		);
	}

	renderCreateModal() {
		const { showCreate, newTokenName, newTokenExpiry, selectedPerms, createLoading, createError } = this.state;
		if (!showCreate) return null;

		const allChecked = this.isAllChecked();

		return (
			<div className={style.modalOverlay} onClick={() => this.setState({ showCreate: false })}>
				<div className={style.modalWide} onClick={(e) => e.stopPropagation()}>
					<div className={style.modalHeader}>
						<h3>Create API Token</h3>
						<button className={style.closeButton} onClick={() => this.setState({ showCreate: false })}>
							&times;
						</button>
					</div>
					<div className={style.modalBody}>
						{createError && <div className={style.errorBanner}>{createError}</div>}

						<div className={style.formRow}>
							<div className={style.formField}>
								<label>Display Name</label>
								<input
									type="text"
									value={newTokenName}
									onChange={(e) => this.setState({ newTokenName: e.target.value })}
									placeholder="e.g. CI Pipeline Token"
								/>
							</div>
							<div className={style.formField}>
								<label>Expires In</label>
								<select
									value={newTokenExpiry}
									onChange={(e) => this.setState({ newTokenExpiry: e.target.value })}
								>
									{EXPIRY_OPTIONS.map((o) => (
										<option key={o.value} value={o.value}>{o.label}</option>
									))}
								</select>
							</div>
						</div>

						<div className={style.formField}>
							<label>API Permissions</label>
							<table className={style.crudGrid}>
								<thead>
									<tr>
										<th className={style.crudResourceCol}>
											<input
												type="checkbox"
												checked={allChecked}
												onChange={(e) => this.toggleAllPerms(e.target.checked)}
											/>
											<span>Resource</span>
										</th>
										{API_OPERATIONS.map((op) => (
											<th key={op} className={style.crudOpCol}>
												<input
													type="checkbox"
													checked={this.isColChecked(op)}
													onChange={(e) => this.toggleOperationCol(op, e.target.checked)}
												/>
												{op.charAt(0).toUpperCase() + op.slice(1)}
											</th>
										))}
									</tr>
								</thead>
								<tbody>
									{API_RESOURCES.map((r) => (
										<tr key={r.key}>
											<td className={style.crudResourceCell}>
												<input
													type="checkbox"
													checked={this.isRowChecked(r.key)}
													onChange={(e) => this.toggleResourceRow(r.key, e.target.checked)}
												/>
												<span>{r.label}</span>
											</td>
											{API_OPERATIONS.map((op) => {
												const permKey = `api:${r.key}:${op}`;
												return (
													<td key={op} className={style.crudCheckCell}>
														<input
															type="checkbox"
															checked={!!selectedPerms[permKey]}
															onChange={(e) => {
																this.setState((prev) => ({
																	selectedPerms: {
																		...prev.selectedPerms,
																		[permKey]: e.target.checked,
																	},
																}));
															}}
														/>
													</td>
												);
											})}
										</tr>
									))}
								</tbody>
							</table>
						</div>
					</div>
					<div className={style.modalFooter}>
						<button className={style.cancelBtn} onClick={() => this.setState({ showCreate: false })}>
							Cancel
						</button>
						<button
							className={style.submitBtn}
							disabled={createLoading}
							onClick={() => this.handleCreateToken()}
						>
							{createLoading ? 'Creating...' : 'Create Token'}
						</button>
					</div>
				</div>
			</div>
		);
	}

	renderRevealModal() {
		const { revealToken } = this.state;
		if (!revealToken) return null;

		return (
			<div className={style.modalOverlay}>
				<div className={style.modal} onClick={(e) => e.stopPropagation()}>
					<div className={style.modalHeader}>
						<h3>Token Created</h3>
					</div>
					<div className={style.modalBody}>
						<div className={style.tokenReveal}>
							<p>Your API token has been created. Copy it now — it will not be shown again.</p>
							<code>{revealToken}</code>
							<button
								className={style.actionBtn}
								onClick={() => {
									navigator.clipboard.writeText(revealToken);
								}}
							>
								Copy to Clipboard
							</button>
						</div>
						<p className={style.tokenWarning}>
							This token will not be shown again. Store it securely.
						</p>
					</div>
					<div className={style.modalFooter}>
						<button
							className={style.submitBtn}
							onClick={() => this.setState({ revealToken: null })}
						>
							Done
						</button>
					</div>
				</div>
			</div>
		);
	}

	render() {
		const { activeTab, error } = this.state;

		return (
			<div className={style.container}>
				<div className={style.header}>
					<h2>My Profile</h2>
				</div>

				{error && <div className={style.errorBanner}>{error}</div>}

				<div className={style.tabs}>
					<button
						className={`${style.tab} ${activeTab === 'password' ? style.tabActive : ''}`}
						onClick={() => this.setState({ activeTab: 'password' })}
					>
						Change Password
					</button>
					<button
						className={`${style.tab} ${activeTab === 'tokens' ? style.tabActive : ''}`}
						onClick={() => this.setState({ activeTab: 'tokens' })}
					>
						API Tokens
					</button>
				</div>

				{activeTab === 'password' && this.renderPasswordTab()}
				{activeTab === 'tokens' && this.renderTokensTab()}

				{this.renderCreateModal()}
				{this.renderRevealModal()}
			</div>
		);
	}
}

export default UserProfile;
